import { Widget } from "@foretagsplatsen/widgetjs";
import PageOverlayWidget from "../PageOverlayWidget.js";

/**
 * This class renders some content modally. The content consists of a
 * header, a body and a footer. While the dialog is visible, the rest
 * of the page can't be used and is darker.
 *
 * @param {Renderable} title - Usually some text to describe what the
 * dialog is about. A small icon is added to the right of the title to
 * close the modal.
 * @param {Renderable} [subtitle] - Small text rendered below the title.
 *   It should probably be used to describe the goal of the dialog
 * @param {Renderable} body - Any content constituting the main part
 * of the dialog.
 * @param {function} footerBuilder - Takes an argument and returns a renderable,
 * usually consisting of buttons and links. The argument is an object of the form
 * { hide: <function>} where `hide` is a function hiding the dialog when called.
 * @param {string} [contentCssClass] - Optional css class added to the content div
 * container.
 * @param {string} [dataTestName] - String used as data-test attribute
 * to facilitate UI testing.
 */
export default class NewDialog extends Widget {
	constructor({
		title,
		subtitle,
		body,
		footerBuilder,
		contentCssClass = "",
		dataTestName = "",
	}) {
		super(...arguments);

		this._title = title;
		this._subtitle = subtitle;
		this._body = body;
		this._footerBuilder = footerBuilder;
		this._contentCssClass = contentCssClass;
		this._dataTestName = dataTestName;

		this._overlay = new PageOverlayWidget({ contents: this });
	}

	show() {
		this._overlay.show();
	}

	hide() {
		this._overlay.hide();
	}

	renderContentOn(html) {
		html.div(
			{
				class: "modal-dialog",
				"data-test": "dialog",
			},
			html.div(
				{
					class: ["modal-content", this._contentCssClass],
					"data-test": this._dataTestName,
				},
				(html) => {
					this._renderHeaderOn(html);
					this._renderBodyOn(html);
					this._renderFooterOn(html);
				},
			),
		);
	}

	_renderHeaderOn(html) {
		if (!this._title) {
			return;
		}

		html.div(
			{ class: "modal-header" },
			html.div(
				html.a({ klass: "close", click: () => this.hide() }, "×"),
				html.h3(this._title),
				this._subtitle
					? html.h6({ class: "subtitle" }, this._subtitle)
					: "",
			),
		);
	}

	_renderBodyOn(html) {
		html.div({ class: "modal-body" }, this._body);
	}

	_renderFooterOn(html) {
		if (!this._footerBuilder) {
			return;
		}

		html.div(
			{ class: "modal-footer" },
			this._footerBuilder({ hide: () => this.hide() }),
		);
	}
}
